import { useState } from "react";

export default function VisierSecurityTesting() {
  const [variationCode, setVariationCode] = useState("CPEH");
  const [testEmployeeIds, setTestEmployeeIds] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState(null);

  const API_BASE = process.env.REACT_APP_API_BASE || "";

  // Pre-configured test employees for each variation (randomly selected from Excel file)
  const variationEmployees = {
    "CPEH": { id: "21074845", name: "Thomas Brynildson", email: "thomas.brynildson@kochcc.com" },
    "CPEX": { id: "21149044", name: "Katarzyna Jarosz", email: "katarzyna.jarosz@molex.com" },
    "CPXH": { id: "21195354", name: "Ewa Dawiec", email: "ewa.dawiec@molex.com" },
    "CPXX": { id: "21261258", name: "Summer Pham", email: "summer.pham@molex.com" },
    "CXEH": { id: "20104660", name: "Stephanie O'Connor", email: "stephanie.oconnor@kochcc.com" },
    "CXXH": { id: "20064969", name: "Jacque Hall", email: "jacque.hall@kochcc.com" },
    "CXXX": { id: "21101375", name: "Jaityn Jackson", email: "jaityn.jackson@kochcc.com" },
    "XPEH": { id: "10005331", name: "Ellison Siple", email: "ellison.siple@kochcc.com" },
    "XPXH": { id: "21190082", name: "Christina Turpin", email: "christina.turpin@kochcc.com" },
    "XPXX": { id: "21068001", name: "Lauren Coleman", email: "lauren.coleman@darkvisiontech.com" },
    "XXEH": { id: "20068623", name: "Leann Hansen", email: "leann.hansen@kochcc.com" },
    "XXEX": { id: "20064806", name: "Sara Monaghen", email: "sara.monaghen@kes.global" },
    "XXXH": { id: "21015795", name: "Mark Engebos", email: "mark.engebos@kbx.com" },
    "XXXX": { id: "21201723", name: "Robbie Wimer", email: "robbie.wimer@kochcc.com" },
  };

  // No default employees - will auto-discover from user's population
  // const defaultTestEmployees = "20137460,26452,20075443,20072684"; // Old hardcoded defaults

  const variationOptions = [
    { value: "CPEH", label: "CPEH", description: "Comp + PII + Exec + HR (full access to all employee data)" },
    { value: "CPEX", label: "CPEX", description: "Comp + PII + Exec + No HR (can see Exec, not HR-restricted)" },
    { value: "CPXH", label: "CPXH", description: "Comp + PII + No Exec + HR (can see HR, not Exec-restricted)" },
    { value: "CPXX", label: "CPXX", description: "Comp + PII + No Exec + No HR (cannot see Exec or HR-restricted)" },
    { value: "CXEH", label: "CXEH", description: "Comp + No PII + Exec + HR (compensation only, no PII)" },
    { value: "CXXH", label: "CXXH", description: "Comp + No PII + No Exec + HR (compensation only, HR access)" },
    { value: "CXXX", label: "CXXX", description: "Comp + No PII + No Exec + No HR (compensation only, restricted)" },
    { value: "XPEH", label: "XPEH", description: "No Comp + PII + Exec + HR (PII only, no compensation)" },
    { value: "XPXH", label: "XPXH", description: "No Comp + PII + No Exec + HR (PII with HR, no compensation/exec)" },
    { value: "XPXX", label: "XPXX", description: "No Comp + PII + No Exec + No HR (PII only, restricted)" },
    { value: "XXEH", label: "XXEH", description: "No Comp + No PII + Exec + HR (basic info only, full access)" },
    { value: "XXEX", label: "XXEX", description: "No Comp + No PII + Exec + No HR (basic info, Exec access)" },
    { value: "XXXH", label: "XXXH", description: "No Comp + No PII + No Exec + HR (basic info, HR access only)" },
    { value: "XXXX", label: "XXXX", description: "No Comp + No PII + No Exec + No HR (basic info only, restricted)" },
  ];

  const handleTest = async () => {
    const employee = variationEmployees[variationCode];
    
    setLoading(true);
    setError("");
    setResults(null);

    try {
      const payload = {
        employee_email: employee.email,  // Use email for impersonation (not ID)
        variation_code: variationCode,
      };
      
      // Only send test employee IDs if user explicitly provided them
      // Otherwise, Lambda will auto-discover employees from each category
      if (testEmployeeIds.trim()) {
        const ids = testEmployeeIds.trim()
          .split(",")
          .map(id => id.trim())
          .filter(id => id);
        
        if (ids.length > 0) {
          payload.test_employee_ids = ids;
        }
      }

      const response = await fetch(`${API_BASE}/visier-security-test`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMsg = data.error || `HTTP ${response.status}`;
        console.error("API Error:", errorMsg, data);
        throw new Error(errorMsg);
      }

      setResults(data);
    } catch (err) {
      const errorMsg = err.message || "Test failed";
      console.error("Test Error:", errorMsg, err);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      "regular": "Regular (No Restrictions)",
      "exec_restrict": "Executive Restricted",
      "hr_restrict": "HR Restricted",
      "both_restrict": "Both E&H Restricted"
    };
    return labels[category] || category.replace(/_/g, " ");
  };

  const currentEmployee = variationEmployees[variationCode];

  return (
    <div className="cug-root sft-root">
      {/* Header matching other tabs */}
      <div className="cug-header">
        <div className="cug-header-top">
          <div className="logo-mark">V</div>
          <div>
            <div className="cug-title">Visier Security Testing</div>
            <div className="cug-subtitle">Automated permission profile validation via impersonation API</div>
          </div>
        </div>
      </div>

      <div className="sft-content">
        {/* Configuration Form - inline style like SecurityFieldTesting */}
        <div className="sft-inline-form">
          <div className="sft-form-row">
            <div className="sft-form-field">
              <label className="sft-label">Permission Variation</label>
              <select
                className="sft-select sft-select-inline"
                value={variationCode}
                onChange={(e) => setVariationCode(e.target.value)}
                disabled={loading}
              >
                {variationOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="sft-form-field">
              <label className="sft-label">
                Test Employee IDs
                <span style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginLeft: "0.5rem" }}>Optional</span>
              </label>
              <input
                className="sft-select sft-select-inline"
                type="text"
                value={testEmployeeIds}
                onChange={(e) => setTestEmployeeIds(e.target.value)}
                placeholder="Leave blank to auto-discover employees"
                disabled={loading}
              />
            </div>

            <button
              className="sft-run-btn-inline"
              onClick={handleTest}
              disabled={loading}
            >
              {loading ? "Running..." : "Run Test"}
            </button>
          </div>

          {/* Info about variation */}
          <div style={{ marginTop: "0.5rem", padding: "0 0.5rem" }}>
            <p style={{ fontSize: "0.875rem", color: "var(--text-muted)" }}>
              {variationOptions.find(o => o.value === variationCode)?.description}
            </p>
          </div>
        </div>

        {/* Impersonation employee info card */}
        <div className="sft-info-card">
          <div className="sft-info-header">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            <span>Impersonation User</span>
          </div>
          <div className="sft-info-content">
            <div className="sft-info-row">
              <span className="sft-info-label">Name:</span>
              <span className="sft-info-value">{currentEmployee.name}</span>
            </div>
            <div className="sft-info-row">
              <span className="sft-info-label">Employee ID:</span>
              <span className="sft-info-value"><code>{currentEmployee.id}</code></span>
            </div>
            <div className="sft-info-row">
              <span className="sft-info-label">Email:</span>
              <span className="sft-info-value">{currentEmployee.email}</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="sft-error-banner">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span>{error}</span>
          </div>
        )}

        {/* Results Section */}
        {results && (
          <>
            {/* Status Banner */}
            <div className={`sft-status-banner ${results.test_summary.failed === 0 ? "sft-status-pass" : "sft-status-fail"}`}>
              <div className="sft-status-icon">
                {results.test_summary.failed === 0 ? (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                ) : (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="15" y1="9" x2="9" y2="15"/>
                    <line x1="9" y1="9" x2="15" y2="15"/>
                  </svg>
                )}
              </div>
              <div className="sft-status-content">
                <h3 className="sft-status-title">
                  {results.test_summary.failed === 0 ? "✅ All Tests Passed" : "❌ Security Validation Failed"}
                </h3>
                <p className="sft-status-text">
                  Tested {results.test_summary.total} employees with {results.impersonated_user.variation} profile as {currentEmployee.name}
                </p>
                <p className="sft-status-text">
                  {results.test_summary.passed} checks passed, {results.test_summary.failed} violations found
                </p>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="sft-summary-grid">
              <div className="sft-summary-card sft-card-employees">
                <div className="sft-summary-value">{results.test_summary.total}</div>
                <div className="sft-summary-label">Employees Tested</div>
              </div>
              <div className="sft-summary-card sft-card-comp">
                <div className="sft-summary-value">{results.test_summary.passed}</div>
                <div className="sft-summary-label">Tests Passed</div>
              </div>
              <div className="sft-summary-card sft-card-pii">
                <div className="sft-summary-value">{results.test_summary.failed}</div>
                <div className="sft-summary-label">Tests Failed</div>
              </div>
              <div className="sft-summary-card sft-card-classes">
                <div className="sft-summary-value">{results.test_summary.success_rate}</div>
                <div className="sft-summary-label">Success Rate</div>
              </div>
            </div>

            {/* Passed Tests Section - Horizontal List */}
            {results.test_summary.passed > 0 && (
              <div className="sft-passed-tests">
                <h4 className="sft-passed-title">✅ Passed Validations</h4>
                <div className="sft-passed-list">
                  {results.results.filter(r => r.passed).map((result, idx) => (
                    <div key={idx} className="sft-passed-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      <span>
                        {result.employee_id} ({getCategoryLabel(result.category)}): {result.actual.has_basic_info ? "Basic ✓" : ""} {result.actual.has_compensation ? "Comp ✓" : ""} {result.actual.has_pii ? "PII ✓" : ""}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Failed Tests Section */}
            {results.test_summary.failed > 0 && (
              <div className="sft-violations">
                <h4 className="sft-violations-title">🚨 Security Violations - Permission Mismatches</h4>
                <div className="sft-violations-list">
                  {results.results.filter(r => !r.passed).map((result, idx) => (
                    <div key={idx} className="sft-violation-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="15" y1="9" x2="9" y2="15"/>
                        <line x1="9" y1="9" x2="15" y2="15"/>
                      </svg>
                      {result.details?.basic_info?.first_name} {result.details?.basic_info?.last_name} ({result.employee_id}) - {getCategoryLabel(result.category)}: 
                      {result.mismatches.basic_info && " Basic Info Mismatch"}
                      {result.mismatches.compensation && " Compensation Mismatch"}
                      {result.mismatches.pii && " PII Mismatch"}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Detailed Results - Employee Cards */}
            <div className="sft-employee-results">
              <h4 className="sft-section-title">Detailed Employee Test Results</h4>
              <p className="sft-section-description">Expand any employee to see exactly which fields were tested and their values</p>
              {results.results.map((result, idx) => (
                <EmployeeTestCard key={idx} result={result} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Detailed Employee Test Card Component
function EmployeeTestCard({ result }) {
  const [expanded, setExpanded] = useState(false);
  
  const getCategoryLabel = (category) => {
    const labels = {
      "regular": "Regular (No Restrictions)",
      "exec_restrict": "Executive Restricted",
      "hr_restrict": "HR Restricted",
      "both_restrict": "Both E&H Restricted"
    };
    return labels[category] || category.replace(/_/g, " ");
  };
  
  const getCategoryBadgeClass = (category) => {
    const classes = {
      "regular": "sft-badge-regular",
      "exec_restrict": "sft-badge-exec",
      "hr_restrict": "sft-badge-hr",
      "both_restrict": "sft-badge-both"
    };
    return classes[category] || "sft-badge-regular";
  };

  // Handle no access case
  if (result.no_access) {
    const getCategoryMessage = (category) => {
      const messages = {
        "regular": "No Regular Employees",
        "exec_restrict": "No Executive Restricted Employees",
        "hr_restrict": "No HR Restricted Employees",
        "both_restrict": "No E&H Restricted Employees"
      };
      return messages[category] || "No Employees in Category";
    };

    return (
      <div className="sft-employee-card" style={{ opacity: 0.7, borderLeft: "4px solid #999" }}>
        <div className="sft-employee-header">
          <div className="sft-employee-info">
            <div className="sft-employee-name" style={{ color: "#666" }}>
              {getCategoryMessage(result.category)} for this user
            </div>
            <span className={`sft-employee-type ${getCategoryBadgeClass(result.category)}`}>
              {getCategoryLabel(result.category)}
            </span>
          </div>
          <div className="sft-employee-stats">
            <button
              className="sft-stat-badge"
              style={{ backgroundColor: "#f0f0f0", color: "#666" }}
              title="User has no employees in this category to test"
            >
              ⊘ Not Available
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sft-employee-card">
      <div className="sft-employee-header">
        <div className="sft-employee-info">
          <div className="sft-employee-name">
            {result.details?.basic_info?.first_name || "Unknown"} {result.details?.basic_info?.last_name || ""}
          </div>
          <span className="sft-employee-id">Employee {result.employee_id}</span>
          <span className={`sft-employee-type ${getCategoryBadgeClass(result.category)}`}>
            {getCategoryLabel(result.category)}
          </span>
        </div>
        <div className="sft-employee-stats">
          <button
            className={`sft-stat-badge ${result.actual.has_basic_info ? (result.expected.basic_info ? 'sft-stat-pass' : 'sft-stat-violation') : 'sft-stat-blocked'}`}
            title="Basic Information Access"
          >
            {result.actual.has_basic_info ? "✓" : "✗"} Basic Info
          </button>
          <button
            className={`sft-stat-badge ${result.actual.has_compensation ? (result.expected.compensation ? 'sft-stat-pass' : 'sft-stat-violation') : 'sft-stat-blocked'}`}
            title="Compensation Access"
          >
            {result.actual.has_compensation ? "✓" : "✗"} Compensation
          </button>
          <button
            className={`sft-stat-badge ${result.actual.has_pii ? (result.expected.pii ? 'sft-stat-pass' : 'sft-stat-violation') : 'sft-stat-blocked'}`}
            title="PII Access"
          >
            {result.actual.has_pii ? "✓" : "✗"} PII
          </button>
          <button
            className="sft-expand-btn"
            onClick={() => setExpanded(!expanded)}
            title={expanded ? "Collapse details" : "Expand details"}
          >
            {expanded ? "▼" : "▶"} {expanded ? "Hide" : "View"} Details
          </button>
        </div>
      </div>
      
      {expanded && (
        <div className="sft-employee-details">
          <div className="sft-details-grid">
            <div className="sft-detail-section">
              <h5 className="sft-detail-title">Expected Access</h5>
              <ul className="sft-detail-list">
                <li className={result.expected.basic_info ? "sft-expected-yes" : "sft-expected-no"}>
                  {result.expected.basic_info ? "✓" : "✗"} Basic Info: {result.expected.basic_info ? "Yes" : "No"}
                </li>
                <li className={result.expected.compensation ? "sft-expected-yes" : "sft-expected-no"}>
                  {result.expected.compensation ? "✓" : "✗"} Compensation: {result.expected.compensation ? "Yes" : "No"}
                </li>
                <li className={result.expected.pii ? "sft-expected-yes" : "sft-expected-no"}>
                  {result.expected.pii ? "✓" : "✗"} PII: {result.expected.pii ? "Yes" : "No"}
                </li>
              </ul>
            </div>
            
            <div className="sft-detail-section">
              <h5 className="sft-detail-title">Basic Information</h5>
              <ul className="sft-detail-list">
                <li>
                  <strong>Employee ID:</strong> 
                  <span style={{ color: "var(--text-secondary)" }}>
                    {result.employee_id}
                  </span>
                </li>
                <li>
                  <strong>First Name:</strong> 
                  <span style={{ color: "var(--text-secondary)" }}>
                    {result.details?.basic_info?.first_name || "N/A"}
                  </span>
                </li>
                <li>
                  <strong>Last Name:</strong> 
                  <span style={{ color: "var(--text-secondary)" }}>
                    {result.details?.basic_info?.last_name || "N/A"}
                  </span>
                </li>
                <li>
                  <strong>Email:</strong> 
                  <span style={{ color: "var(--text-secondary)" }}>
                    {result.details?.basic_info?.email || "N/A"}
                  </span>
                </li>
                <li>
                  <strong>Position:</strong> 
                  <span style={{ color: "var(--text-secondary)" }}>
                    {result.details?.basic_info?.position || "N/A"}
                  </span>
                </li>
              </ul>
            </div>
            
            <div className="sft-detail-section">
              <h5 className="sft-detail-title">Compensation Data</h5>
              <ul className="sft-detail-list">
                <li>
                  <strong>Base Pay:</strong> 
                  <span className={result.details?.compensation?.base_pay && result.details.compensation.base_pay !== 0 ? "sft-visible" : "sft-not-visible"}>
                    {result.details?.compensation?.base_pay && result.details.compensation.base_pay !== 0 ? "Visible" : "Not Visible"}
                  </span>
                </li>
                <li>
                  <strong>Monthly Rate:</strong> 
                  <span className={result.details?.compensation?.monthly_rate && result.details.compensation.monthly_rate !== 0 ? "sft-visible" : "sft-not-visible"}>
                    {result.details?.compensation?.monthly_rate && result.details.compensation.monthly_rate !== 0 ? "Visible" : "Not Visible"}
                  </span>
                </li>
                <li>
                  <strong>Hourly Rate:</strong> 
                  <span className={result.details?.compensation?.hourly_rate && result.details.compensation.hourly_rate !== 0 ? "sft-visible" : "sft-not-visible"}>
                    {result.details?.compensation?.hourly_rate && result.details.compensation.hourly_rate !== 0 ? "Visible" : "Not Visible"}
                  </span>
                </li>
              </ul>
            </div>
            
            <div className="sft-detail-section">
              <h5 className="sft-detail-title">PII Data</h5>
              <ul className="sft-detail-list">
                <li>
                  <strong>Birth Date:</strong> 
                  <span className={result.details?.pii?.birth_date ? "sft-visible" : "sft-not-visible"}>
                    {result.details?.pii?.birth_date ? "Visible" : "Not Visible"}
                  </span>
                </li>
                <li>
                  <strong>Gender:</strong> 
                  <span className={result.details?.pii?.gender && result.details.pii.gender !== "" ? "sft-visible" : "sft-not-visible"}>
                    {result.details?.pii?.gender && result.details.pii.gender !== "" ? "Visible" : "Not Visible"}
                  </span>
                </li>
                <li>
                  <strong>Ethnicity:</strong> 
                  <span className={result.details?.pii?.ethnicity && result.details.pii.ethnicity !== "" && result.details.pii.ethnicity !== "-1.0" ? "sft-visible" : "sft-not-visible"}>
                    {result.details?.pii?.ethnicity && result.details.pii.ethnicity !== "" && result.details.pii.ethnicity !== "-1.0" ? "Visible" : "Not Visible"}
                  </span>
                </li>
                <li>
                  <strong>Veteran Status:</strong> 
                  <span className={result.details?.pii?.veteran_status && result.details.pii.veteran_status !== "" ? "sft-visible" : "sft-not-visible"}>
                    {result.details?.pii?.veteran_status && result.details.pii.veteran_status !== "" ? "Visible" : "Not Visible"}
                  </span>
                </li>
                <li>
                  <strong>Disability Status:</strong> 
                  <span className={result.details?.pii?.disability_status && result.details.pii.disability_status !== "" ? "sft-visible" : "sft-not-visible"}>
                    {result.details?.pii?.disability_status && result.details.pii.disability_status !== "" ? "Visible" : "Not Visible"}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
